import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Empty,
  Progress,
  Space,
  Tag,
  Typography,
} from 'antd';
import { Link } from 'react-router-dom';
import type { RouteViewModel, RouteViewModelDocuments } from '../../types/routeViewModel';
import type { WorkspaceTask } from '../../store/api/workspaceApi';
import TaskCard from '../tasks/TaskCard';
import { buildClientShortNameLookupFromRouteApplications } from '../../domain/scopedTaskPresentation';
import { WorkspaceRouteStopDatesCell } from '../workspace/WorkspaceRouteStopDatesCell';
import dayjs from 'dayjs';
import { StatusCell, getStatusLabel } from '../../ui';
import { obligationStageLabel } from '../../utils/obligationStageUi';
import { projectRouteCarrierSettlement } from '../../utils/carrierSettlementProjection';
import {
  formatVolumeM3,
  formatWeightKg,
  projectRouteApplications,
  projectRouteCargoSummary,
  projectRouteDrawerExpenseObligations,
  projectRouteExecutionDates,
  projectRouteExecutionItinerary,
  projectRouteProblems,
  projectRouteTransport,
} from './routeContextDrawerProjection';
import type { RouteViewModelObligationRow } from '../../types/routeViewModel';

const { Text } = Typography;

function formatRub(amount: number | null | undefined): string {
  const n = Number.isFinite(Number(amount)) ? Math.round(Number(amount)) : 0;
  return `${n.toLocaleString('ru-RU')} ₽`;
}

function docReqLabel(key: string): string {
  const map: Record<string, string> = {
    LOADING_PHOTO: 'Фото погрузки',
    UNLOADING_PHOTO: 'Фото выгрузки',
    CARRIER_INVOICE: 'Счёт перевозчика',
    ORIGINAL_TN: 'Оригинал ТН',
    CARGO_DOCS: 'Документы по грузу',
  };
  return map[key] || key;
}

function docStatusTag(status: string): React.ReactNode {
  const label = getStatusLabel(status);
  const color =
    status === 'OVERDUE' ? 'error' : status === 'EXPECTED' ? 'warning' : status === 'SATISFIED' ? 'success' : 'default';
  return <Tag color={color}>{label}</Tag>;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY') : '—';
}

function ObligationDrawerBlock({
  obligation,
  formatRub,
  formatDate: fmtDate,
}: {
  obligation: RouteViewModelObligationRow;
  formatRub: (amount: number | null | undefined) => string;
  formatDate: (value: string | null | undefined) => string;
}) {
  const payments = obligation.payments ?? [];
  const paidAt = obligation.paidAt ? fmtDate(obligation.paidAt) : '—';

  return (
    <div style={{ fontSize: 12, lineHeight: 1.55, padding: '8px 10px', border: '1px solid #f0f0f0', borderRadius: 6 }}>
      <Text strong style={{ display: 'block', marginBottom: 4 }}>
        {obligationStageLabel(obligation.stage)}
      </Text>
      <Text style={{ display: 'block' }}>Сумма: {formatRub(obligation.amountWithVAT)}</Text>
      <Text style={{ display: 'block' }}>Остаток: {formatRub(obligation.remainingAmount)}</Text>
      <Text style={{ display: 'block' }}>Срок оплаты: {fmtDate(obligation.dueDate)}</Text>
      <Text style={{ display: 'block' }}>Дата оплаты: {paidAt}</Text>
      <Text style={{ display: 'block' }}>Статус: {getStatusLabel(obligation.status)}</Text>
      {payments.length > 0 ? (
        <div style={{ marginTop: 6 }}>
          <Text type="secondary" style={{ display: 'block' }}>
            Платежи:
          </Text>
          {payments.map((payment) => (
            <Text key={payment.id} style={{ display: 'block', paddingLeft: 8 }}>
              {fmtDate(payment.paymentDate)} — {formatRub(payment.amountPaid)}
            </Text>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export interface RouteContextDrawerRoutePanelProps {
  routeView: RouteViewModel;
  drawerOperationalTasks: WorkspaceTask[];
  drawerFinanceTasks: WorkspaceTask[];
  overdueFinanceInContext: boolean;
  overdueOperationalInContext: boolean;
  onInlineTaskAction: (task: WorkspaceTask) => void;
  onClose: () => void;
  onOpenFullCard: () => void;
}

export const RouteContextDrawerRoutePanel: React.FC<RouteContextDrawerRoutePanelProps> = ({
  routeView,
  drawerOperationalTasks,
  drawerFinanceTasks,
  overdueFinanceInContext,
  overdueOperationalInContext,
  onInlineTaskAction,
  onClose,
  onOpenFullCard,
}) => {
  const [appsExpanded, setAppsExpanded] = useState(false);

  const routeLabel = `#${routeView.operation.id}`;
  const direction = routeView.operation.direction?.label?.trim() || '—';
  const applications = useMemo(() => projectRouteApplications(routeView.applications), [routeView.applications]);
  const problems = useMemo(() => projectRouteProblems(routeView), [routeView]);
  const transport = useMemo(() => projectRouteTransport(routeView), [routeView]);
  const execution = useMemo(() => projectRouteExecutionDates(routeView), [routeView]);
  const cargo = useMemo(() => projectRouteCargoSummary(routeView), [routeView]);
  const itinerary = useMemo(() => projectRouteExecutionItinerary(routeView), [routeView]);
  const carrierSettlement = useMemo(() => projectRouteCarrierSettlement(routeView), [routeView]);
  const contractNumber = routeView.operation.carrierContractRequest?.number?.trim() || '';
  const contractDate = formatDate(routeView.operation.carrierContractRequest?.date ?? null);
  const carrierContractLine = contractNumber
    ? contractDate !== '—'
      ? `№${contractNumber} от ${contractDate}`
      : `№${contractNumber}`
    : null;
  const dashboard = routeView.executionDashboard;
  const finance = routeView.finance;
  const documentReqs = (routeView.documents as RouteViewModelDocuments)?.requirements?.slice(0, 8) ?? [];
  const obligationRows = useMemo(
    () => projectRouteDrawerExpenseObligations(finance),
    [finance]
  );

  const taskPresentationContext = useMemo(
    () => ({
      clientShortNameByGroupId: buildClientShortNameLookupFromRouteApplications(
        routeView.applications,
        routeView.structure?.applicationGroups
      ),
    }),
    [routeView.applications, routeView.structure?.applicationGroups]
  );

  const visibleApps = appsExpanded ? applications : applications.slice(0, 3);
  const hiddenAppsCount = Math.max(0, applications.length - visibleApps.length);

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {problems.length > 0 ? (
        <Alert
          type="error"
          showIcon
          message="Проблемы и риски"
          description={
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {problems.map((item) => (
                <Tag
                  key={`${item.level}_${item.message}`}
                  color={item.level === 'critical' ? 'error' : item.level === 'warning' ? 'warning' : 'default'}
                >
                  {item.message}
                </Tag>
              ))}
            </Space>
          }
        />
      ) : null}

      <div>
        <Text strong style={{ fontSize: 14, display: 'block' }}>
          {routeLabel} · {direction}
        </Text>
        <div style={{ marginTop: 6 }}>
          <StatusCell status={routeView.operation.status} />
        </div>
        <Space wrap size={6} style={{ marginTop: 6 }}>
          {overdueFinanceInContext ? <Tag color="error">Просрочены фин. задачи</Tag> : null}
          {overdueOperationalInContext ? <Tag color="warning">Просрочены опер. задачи</Tag> : null}
        </Space>
      </div>

      <div>
        <Text strong style={{ fontSize: 13 }}>
          Заявки
        </Text>
        {applications.length === 0 ? (
          <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
            —
          </Text>
        ) : (
          <Space direction="vertical" size={6} style={{ width: '100%', marginTop: 6 }}>
            {visibleApps.map((row) => (
              <div key={row.applicationId}>
                <Link to={`/applications/${row.applicationId}`} onClick={(e) => e.stopPropagation()}>
                  {row.number}
                </Link>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  {row.clientName}
                </Text>
              </div>
            ))}
            {hiddenAppsCount > 0 ? (
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setAppsExpanded(true)}>
                Показать ещё {hiddenAppsCount}
              </Button>
            ) : null}
            {appsExpanded && applications.length > 3 ? (
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setAppsExpanded(false)}>
                Свернуть
              </Button>
            ) : null}
          </Space>
        )}
      </div>

      <Descriptions size="small" column={1} bordered title="Транспорт" labelStyle={{ width: 130 }}>
        <Descriptions.Item label="Перевозчик">{transport.carrier}</Descriptions.Item>
        <Descriptions.Item label="Тягач">{transport.vehicle}</Descriptions.Item>
        <Descriptions.Item label="Прицеп">{transport.trailer}</Descriptions.Item>
        <Descriptions.Item label="Водитель">{transport.driver}</Descriptions.Item>
        {carrierContractLine ? (
          <Descriptions.Item label="Договор-заявка перевозчика">{carrierContractLine}</Descriptions.Item>
        ) : null}
      </Descriptions>

      <Descriptions size="small" column={1} bordered title="Исполнение" labelStyle={{ width: 130 }}>
        <Descriptions.Item label="Погрузка">
          <WorkspaceRouteStopDatesCell summary={execution.loading} />
        </Descriptions.Item>
        <Descriptions.Item label="Выгрузка">
          <WorkspaceRouteStopDatesCell summary={execution.unloading} />
        </Descriptions.Item>
        <Descriptions.Item label="Точки">
          Погрузки: {execution.stopCounts.loading || (execution.loading.stopCount > 0 ? execution.loading.stopCount : 1)} ·
          Выгрузки:{' '}
          {execution.stopCounts.unloading || (execution.unloading.stopCount > 0 ? execution.unloading.stopCount : 1)}
        </Descriptions.Item>
        {dashboard ? (
          <>
            <Descriptions.Item label="Прогресс">
              <Progress percent={dashboard.executionProgressPercent ?? 0} size="small" />
            </Descriptions.Item>
            <Descriptions.Item label="Точки (факт)">
              {dashboard.stopsSummary.completed}/{dashboard.stopsSummary.total}
            </Descriptions.Item>
          </>
        ) : null}
      </Descriptions>

      {itinerary.length > 0 ? (
        <div>
          <Text strong style={{ fontSize: 13 }}>
            Маршрут исполнения
          </Text>
          <Space direction="vertical" size={10} style={{ width: '100%', marginTop: 8 }}>
            {itinerary.slice(0, 8).map((stop) => (
              <div
                key={`${stop.position}_${stop.city}_${stop.addressFull}`}
                style={{ fontSize: 12, lineHeight: 1.55, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}
              >
                <Text strong style={{ display: 'block' }}>
                  {stop.kindLabel}
                </Text>
                {stop.applicationLabel ? (
                  <Text style={{ display: 'block' }}>{stop.applicationLabel}</Text>
                ) : null}
                <Text style={{ display: 'block' }}>{stop.city}</Text>
                <Text type="secondary" style={{ display: 'block' }}>
                  {stop.addressFull}
                </Text>
                <Text style={{ display: 'block', marginTop: 4 }}>
                  План: {stop.plannedDate ?? '—'}
                </Text>
                <Text style={{ display: 'block' }}>
                  Факт: {stop.actualDate ?? '—'}
                </Text>
                <Text style={{ display: 'block' }}>
                  Статус: {stop.statusLabel}
                </Text>
              </div>
            ))}
            {itinerary.length > 8 ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                …ещё {itinerary.length - 8} точек
              </Text>
            ) : null}
          </Space>
        </div>
      ) : null}

      <Descriptions size="small" column={1} bordered title="Грузы" labelStyle={{ width: 130 }}>
        <Descriptions.Item label="Позиций">{cargo.lineCount || '—'}</Descriptions.Item>
        <Descriptions.Item label="Мест">{cargo.totalQuantityPlaces || '—'}</Descriptions.Item>
        <Descriptions.Item label="Вес">{formatWeightKg(cargo.totalWeightKg)}</Descriptions.Item>
        <Descriptions.Item label="Объём">{formatVolumeM3(cargo.totalVolumeM3)}</Descriptions.Item>
      </Descriptions>

      <Descriptions size="small" column={1} bordered title="Расчёты с перевозчиком" labelStyle={{ width: 130 }}>
        <Descriptions.Item label="Перевозчик">{carrierSettlement.carrierName}</Descriptions.Item>
        <Descriptions.Item label="Ставка">
          {carrierSettlement.rateAmount != null ? formatRub(carrierSettlement.rateAmount) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Оплачено">
          {carrierSettlement.totalPaid != null ? formatRub(carrierSettlement.totalPaid) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Остаток">
          {carrierSettlement.remaining != null ? formatRub(carrierSettlement.remaining) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Статус">{carrierSettlement.paymentStatus}</Descriptions.Item>
      </Descriptions>

      {obligationRows.length > 0 ? (
        <div>
          <Text strong style={{ fontSize: 13 }}>
            Обязательства
          </Text>
          <Space direction="vertical" size={10} style={{ width: '100%', marginTop: 8 }}>
            {obligationRows.map((obl) => (
              <ObligationDrawerBlock key={obl.id} obligation={obl} formatRub={formatRub} formatDate={formatDate} />
            ))}
          </Space>
        </div>
      ) : null}

      <Collapse
        size="small"
        defaultActiveKey={['operational', 'finance']}
        items={[
          {
            key: 'operational',
            label: `Операционные задачи (${drawerOperationalTasks.length})`,
            children:
              drawerOperationalTasks.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет открытых опер. задач в очереди" />
              ) : (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {drawerOperationalTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      workspaceTask={task}
                      presentationContext={taskPresentationContext}
                      id={task.id}
                      taskType={task.taskType}
                      status={task.status}
                      compact
                      dueDate={task.dueDate}
                      routeId={task.routeId}
                      applicationId={task.applicationId}
                      entityType={task.entityType}
                      invoiceId={task.invoiceId}
                      obligationId={task.obligationId}
                      obligationStage={task.obligationStage}
                      routeStatus={task.routeStatus}
                      loadingCity={task.loadingCity}
                      unloadingCity={task.unloadingCity}
                      carrierName={task.carrierName}
                      onAction={(t) => {
                        onInlineTaskAction(t);
                        onClose();
                      }}
                    />
                  ))}
                </Space>
              ),
          },
          {
            key: 'finance',
            label: `Финансовые задачи (${drawerFinanceTasks.length})`,
            children:
              drawerFinanceTasks.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет открытых фин. задач в очереди" />
              ) : (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {drawerFinanceTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      workspaceTask={task}
                      presentationContext={taskPresentationContext}
                      id={task.id}
                      taskType={task.taskType}
                      status={task.status}
                      compact
                      dueDate={task.dueDate}
                      routeId={task.routeId}
                      applicationId={task.applicationId}
                      entityType={task.entityType}
                      invoiceId={task.invoiceId}
                      obligationId={task.obligationId}
                      obligationStage={task.obligationStage}
                      routeStatus={task.routeStatus}
                      loadingCity={task.loadingCity}
                      unloadingCity={task.unloadingCity}
                      carrierName={task.carrierName}
                      onAction={(t) => {
                        onInlineTaskAction(t);
                        onClose();
                      }}
                    />
                  ))}
                </Space>
              ),
          },
          ...(documentReqs.length
            ? [
                {
                  key: 'docs',
                  label: 'Документы (требования)',
                  children: (
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      {documentReqs.map((req) => (
                        <div key={req.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <Text style={{ fontSize: 13 }}>{docReqLabel(req.key)}</Text>
                          {docStatusTag(req.status)}
                        </div>
                      ))}
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />

      <Button type="link" size="small" onClick={onOpenFullCard} style={{ paddingLeft: 0 }}>
        Открыть полную карточку…
      </Button>
    </Space>
  );
};

export default RouteContextDrawerRoutePanel;
